/**
 * Small, authored MetaPerson facial acting. This layer never rotates bones,
 * changes anatomy or writes jaw/native viseme channels. Run it after the body
 * mixer, before rendering; there is no second RAF, timer or external clip.
 */
export type LanguageAvatarExpression = 'idle' | 'listening' | 'correct' | 'retry' | 'celebrate';

export interface ExpressionMorphTarget {
  morphTargetDictionary: Record<string, number>;
  morphTargetInfluences: number[];
}

export const EXPRESSION_MORPHS = [
  'eyeBlinkLeft', 'eyeBlinkRight', 'eyeLookInLeft', 'eyeLookInRight',
  'eyeLookOutLeft', 'eyeLookOutRight', 'eyeLookUpLeft', 'eyeLookUpRight',
  'eyeLookDownLeft', 'eyeLookDownRight', 'eyeSquintLeft', 'eyeSquintRight',
  'browInnerUp', 'browOuterUpLeft', 'browOuterUpRight',
  'cheekSquintLeft', 'cheekSquintRight', 'mouthSmileLeft', 'mouthSmileRight',
] as const;
type ExpressionMorph = typeof EXPRESSION_MORPHS[number];
export type ExpressionPose = Record<ExpressionMorph, number>;
const clamp = (value: number, low = 0, high = 1) => Math.max(low, Math.min(high, value));
const smooth = (value: number) => { const x = clamp(value); return x * x * (3 - 2 * x); };
const durations: Partial<Record<LanguageAvatarExpression, number>> = { correct: 2.8, retry: 3.2, celebrate: 4.2 };

/** A reaction settles back to the welcoming rest pose, even if its prop stays set. */
export function reactionEnvelope(expression: LanguageAvatarExpression, elapsed: number): number {
  const duration = durations[expression];
  if (!duration || elapsed < 0 || elapsed >= duration) return 0;
  return smooth(elapsed / .32) * smooth((duration - elapsed) / 1.1);
}

export function expressionSeed(identity: string): number {
  let value = 0;
  for (const character of identity) value = (value * 31 + character.charCodeAt(0)) >>> 0;
  return (value % 1000) / 1000;
}

function blinkPulse(time: number): number {
  if (time < 0 || time >= .2) return 0;
  return time < .075 ? smooth(time / .075) : 1 - smooth((time - .075) / .125);
}

/** Unequal intervals, occasional paired blink; no callback survives unmount. */
export function expressionBlink(elapsed: number, seed = 0): number {
  const intervals = [3.6, 5.3, 4.1, 5.8, 3.4];
  const cycle = intervals.reduce((sum, interval) => sum + interval, 0);
  const time = ((elapsed + seed * 2.4) % cycle + cycle) % cycle;
  let at = 0;
  for (let index = 0; index < intervals.length; index++) {
    at += intervals[index];
    const relative = time - (at - .6);
    const blink = blinkPulse(relative);
    if (blink > 0) return blink;
    if (index === 2) {
      const second = blinkPulse(relative - .32);
      if (second > 0) return second * .92;
    }
  }
  return 0;
}

/** Pure pose sampling enables bounded-state tests and a reproducible render audit. */
export function sampleExpressionPose(
  expression: LanguageAvatarExpression, elapsed: number, reactionTime: number, seed = 0,
): ExpressionPose {
  const pose = Object.fromEntries(EXPRESSION_MORPHS.map(name => [name, 0])) as ExpressionPose;
  const reaction = reactionEnvelope(expression, reactionTime);
  const listening = expression === 'listening';
  const breathe = .5 + .5 * Math.sin(elapsed * .72 + seed * 6);
  let smile = .075 + .018 * breathe;
  let cheek = .018;
  let brow = .018 + .012 * breathe;
  let outerBrow = .008;
  let squint = .01;

  if (listening) { smile = .055; brow = .085; outerBrow = .025; }
  if (expression === 'correct' || expression === 'celebrate') {
    const celebration = expression === 'celebrate';
    // Two soft smiling accents during celebration, with a closed quiet mouth.
    // This is facial acting, not a generated laugh or an audio-driven viseme.
    const accent = celebration ? .88 + .12 * Math.sin(reactionTime * 7) ** 2 : 1;
    smile += reaction * (celebration ? .29 : .22) * accent;
    cheek += reaction * (celebration ? .11 : .075);
    outerBrow += reaction * .065;
    squint += reaction * .045;
  }
  if (expression === 'retry') {
    smile += reaction * .085;
    brow += reaction * .105;
    outerBrow += reaction * .018;
  }

  // Both eyes look together; most of the time the gaze stays close to the user.
  const gazeX = (Math.sin(elapsed * .83 + seed * 5) + .4 * Math.sin(elapsed * 1.63)) * (listening ? .014 : .025);
  const gazeY = Math.sin(elapsed * .57 + seed * 3) * .014;
  pose.eyeLookInLeft = pose.eyeLookOutRight = Math.max(0, gazeX);
  pose.eyeLookOutLeft = pose.eyeLookInRight = Math.max(0, -gazeX);
  pose.eyeLookUpLeft = pose.eyeLookUpRight = Math.max(0, gazeY);
  pose.eyeLookDownLeft = pose.eyeLookDownRight = Math.max(0, -gazeY);
  pose.eyeBlinkLeft = pose.eyeBlinkRight = expressionBlink(elapsed, seed);
  pose.eyeSquintLeft = pose.eyeSquintRight = squint;
  pose.browInnerUp = brow;
  pose.browOuterUpLeft = pose.browOuterUpRight = outerBrow;
  pose.cheekSquintLeft = pose.cheekSquintRight = cheek;
  pose.mouthSmileLeft = smile;
  pose.mouthSmileRight = smile * .97;
  return pose;
}

export function createLanguageExpressionDriver(
  targets: ExpressionMorphTarget[], identity: string, startedAt: number,
) {
  const seed = expressionSeed(identity);
  const bindings = targets.flatMap(target => EXPRESSION_MORPHS.flatMap(name => {
    const index = target.morphTargetDictionary[name];
    return index === undefined ? [] : [{ target, name, index, original: target.morphTargetInfluences[index] ?? 0 }];
  }));
  // MetaPerson's speech path drives PP/FF/etc, not ARKit mouthSmile*. Cílios
  // share the head's smiling/blinking shapes, so all facial meshes move together.
  const nativeVisemes = targets.some(target => target.morphTargetDictionary.PP !== undefined
    && target.morphTargetDictionary.FF !== undefined);
  const current = Object.fromEntries(EXPRESSION_MORPHS.map(name => [name, 0])) as ExpressionPose;
  let expression: LanguageAvatarExpression = 'idle';
  let reactionStarted = startedAt;
  let lastTime = startedAt;
  let disposed = false;

  return {
    update(next: LanguageAvatarExpression, now: number, speechActive = false) {
      if (disposed || !Number.isFinite(now)) return;
      if (next !== expression) { expression = next; reactionStarted = now; }
      const delta = clamp(now - lastTime, 0, .1);
      lastTime = now;
      const pose = sampleExpressionPose(expression, Math.max(0, now - startedAt), Math.max(0, now - reactionStarted), seed);
      for (const name of EXPRESSION_MORPHS) {
        const mouth = name.startsWith('mouth');
        if (mouth && speechActive) {
          // Release the closed smile immediately for MetaPerson's native visemes.
          // For ARKit, even a zero write would compete with a spoken vowel.
          current[name] = 0;
          continue;
        }
        const tau = name.startsWith('eyeBlink') ? .012 : .12;
        current[name] += (pose[name] - current[name]) * (1 - Math.exp(-delta / tau));
      }
      for (const binding of bindings) {
        if (speechActive && binding.name.startsWith('mouth') && !nativeVisemes) continue;
        binding.target.morphTargetInfluences[binding.index] = clamp(current[binding.name]);
      }
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const binding of bindings) binding.target.morphTargetInfluences[binding.index] = binding.original;
    },
  };
}
